import { http } from "./http";
import type { Presence } from "./presence.api";

/*
 * Messagerie avec les élèves, côté équipe (`/v1/student-messaging`).
 * API distincte de la messagerie interne (`messaging.api.ts`) : texte seul, sans temps réel.
 * À l'écran, les deux sont réunies dans le volet Messagerie (`MessagingDock`).
 */

/** OFFICE : boîte commune du secrétariat ; DIRECT : moniteur et élève ; GROUP : équipe et élèves. */
export type StudentConversationKind = "OFFICE" | "DIRECT" | "GROUP";

export type StudentMessagingStaffRole =
  | "owner"
  | "manager"
  | "agent"
  | "read_only"
  | "instructor";

/** Comptes de l'équipe admis par le serveur dans cette messagerie. */
export const STUDENT_MESSAGING_ROLES: readonly string[] = [
  "owner",
  "manager",
  "agent",
  "read_only",
  "instructor",
];

export type StudentMessagingContacts = {
  role: string;
  canOpenOffice: boolean;
  canOpenDirect: boolean;
  canCreateGroup: boolean;
  staff: {
    id: string;
    name: string;
    role: StudentMessagingStaffRole;
    presence?: Presence | null;
  }[];
};

export type StudentSearchResult = { id: string; name: string };

export type StudentConversation = {
  id: string;
  kind: StudentConversationKind;
  title: string;
  candidateId?: string | null;
  /** Présence de l'élève (échange à deux, boîte commune) ; `null` pour un groupe. */
  presence?: Presence | null;
  unread: number;
  updatedAt: string;
  last: { body: string; author: string; at: string } | null;
};

export type StudentMessage = {
  id: string;
  sequence: number;
  mine: boolean;
  /** `assistant` : réponse générée par Permio AI (une IA), au nom de l'auto-école. */
  authorKind: "staff" | "student" | "assistant";
  author: string;
  body: string;
  createdAt: string;
};

export type StudentThread = {
  conversation: {
    id: string;
    kind: StudentConversationKind;
    title: string;
    canWrite: boolean;
    presence?: Presence | null;
  };
  members: {
    kind: "staff" | "student";
    role: string;
    name: string;
    presence?: Presence | null;
  }[];
  /** Du plus ancien au plus récent, 50 au plus. */
  items: StudentMessage[];
  hasMore: boolean;
};

export type StudentGroupPayload = {
  /** UUID v4, le même en cas de nouvel essai. */
  requestId: string;
  title: string;
  staffIds: string[];
  candidateIds: string[];
};

const root = "/v1/student-messaging";

export const studentMessagingApi = {
  contacts: async () =>
    (await http.get<StudentMessagingContacts>(root + "/contacts")).data,
  /** Recherche par nom : 2 caractères au moins, 20 résultats au plus. */
  students: async (search: string, signal?: AbortSignal) =>
    (
      await http.get<StudentSearchResult[]>(root + "/students", {
        params: { search },
        signal,
      })
    ).data,
  /** La plus récente d'abord. */
  conversations: async () =>
    (await http.get<StudentConversation[]>(root + "/conversations")).data,
  /** Ouvre ou retrouve la boîte commune du secrétariat avec cet élève. */
  openOffice: async (candidateId: string) =>
    (
      await http.post<{ id: string }>(root + "/conversations/office", {
        candidateId,
      })
    ).data,
  /** Échange à deux entre le moniteur connecté et cet élève. */
  openDirect: async (candidateId: string) =>
    (
      await http.post<{ id: string }>(root + "/conversations/direct", {
        candidateId,
      })
    ).data,
  createGroup: async (payload: StudentGroupPayload) =>
    (await http.post<{ id: string }>(root + "/conversations/group", payload))
      .data,
  messages: async (id: string, before?: number) =>
    (
      await http.get<StudentThread>(`${root}/conversations/${id}/messages`, {
        params: { before },
      })
    ).data,
  send: async (id: string, requestId: string, body: string) =>
    (
      await http.post<{ id: string; sequence: number }>(
        `${root}/conversations/${id}/messages`,
        { requestId, body },
      )
    ).data,
  /** Nombre d'élèves non archivés qui recevraient un message à tous (secrétariat). */
  broadcastRecipients: async () =>
    (await http.get<{ count: number }>(root + "/broadcasts/recipients")).data,
  /** Message à tous les élèves, dans la boîte du bureau de chacun ; même `requestId` : sans doublon. */
  broadcast: async (requestId: string, body: string) =>
    (
      await http.post<{ id: string; recipients: number; sent: number }>(
        root + "/broadcasts",
        { requestId, body },
      )
    ).data,
  /** Marque lu jusqu'à `sequence` (état partagé par le secrétariat pour une boîte commune). */
  read: async (id: string, sequence: number) =>
    (
      await http.post<{ sequence: number }>(
        `${root}/conversations/${id}/read`,
        { sequence },
      )
    ).data,
};

/** Portée du cache : compte, rôle et école (sélecteur du magasin de session). */
export const studentMessagingScope = (state: {
  user: { id: string; role: string } | null;
  activeSchoolId: string | null;
}) => `${state.user?.id}:${state.user?.role}:${state.activeSchoolId}`;

/** Clé de cache de la liste des conversations avec élèves (volet Messagerie). */
export const studentConversationsKey = (scope: string) =>
  ["student-messaging-list", scope] as const;

/** Nombre de messages non lus, toutes conversations confondues. */
export function countStudentUnread(conversations: unknown): number {
  if (!Array.isArray(conversations)) return 0;
  return conversations.reduce(
    (total: number, conversation: { unread?: unknown }) =>
      total +
      (typeof conversation?.unread === "number" ? conversation.unread : 0),
    0,
  );
}

/** Ce que le volet Messagerie doit ouvrir : une conversation avec élève(s), ou l'échange avec un élève. */
export type StudentMessagingTarget = {
  conversationId?: string;
  /** Boîte commune de l'élève (créée au besoin), ou échange à deux pour un moniteur. */
  candidateId?: string;
  /** « Message à tous les élèves » (secrétariat), ouvert directement. */
  broadcast?: boolean;
};

const OPEN_EVENT = "permio:student-message";

/** Ouvre le volet Messagerie sur une conversation avec élève(s), sans changer de page. */
export function openStudentMessaging(target: StudentMessagingTarget) {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: target }));
}

/** Volet Messagerie : écoute les demandes d'ouverture ; rend la fonction de désabonnement. */
export function onStudentMessagingTarget(
  listener: (target: StudentMessagingTarget) => void,
) {
  const handler = (event: Event) =>
    listener((event as CustomEvent<StudentMessagingTarget>).detail ?? {});
  window.addEventListener(OPEN_EVENT, handler);
  return () => window.removeEventListener(OPEN_EVENT, handler);
}
